import React, { useRef, useState } from 'react';
import { Box, Button, Typography, useTheme, Paper, Stack, Tooltip } from '@mui/material';
import { AddBox, Campaign, Event } from '@mui/icons-material';
import { useOfferList } from '../hooks/useOfferList';
import { DataTable } from '@shared/components/DataTable/DataTable';
import { ConfirmDialog } from '../shared/components/ConfirmDialog/ConfirmDialog';
import { OfferListItem } from '../types';
import { TableSearchBar } from '../components/OfferList/TableSearchBar';
import { useOfferConfig } from '../contexts';
import ActionButtons from '../components/OfferList/ActionButtons';
import { ScheduleDrawer } from '../components/Drawers/ScheduleDrawer';
import { BulkOfferDrawer } from '../components/Drawers/BulkOfferDrawer';
import { OfferDetailsDrawer } from '../components/Drawers/OfferDetailsDrawer';
// import { FloatingChatButton } from '../components/OfferChatbot';
import { formatCurrency, formatDate } from '../utils';
import { getLabelFromOptionId } from '../utils/labelHelpers';
import { PageHeader } from '@shared/components/PageHeader';
import { SyncOnChip } from '../components/SyncOnChip';
import { OfferFiltersDrawer } from '../components/Drawers/OfferFiltersDrawer';
import { usePermission } from '@modules/auth/hooks';
import { Module } from '@modules/admin/types';
import MoreActionsMenu from '../components/OfferList/MoreActionsMenu';

const OfferList: React.FC = () => {
  const theme = useTheme();
  const { canAdd, canUpdate } = usePermission(Module.OFFER);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<OfferListItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkScheduleOpen, setBulkScheduleOpen] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOfferForDetails, setSelectedOfferForDetails] = useState<OfferListItem | null>(null);
  const {
    scopeOptions,
    currencyOptions,
    categoryOptions,
    countryOptions,
    priceOptions,
    isLoading: configLoading,
  } = useOfferConfig();
  const openColumnsMenuRef = useRef<null | ((anchorEl: HTMLElement) => void)>(null);
  const [visibleColsCount, setVisibleColsCount] = useState<number | null>(null);

  const {
    offers,
    totalRows,
    page,
    rowsPerPage,
    orderBy,
    order,
    filters,
    isLoading,
    isError,
    error,
    isUpdatingStatus,
    handlePageChange,
    changePageSize,
    handleSort,
    changeFilters,
    clearFilter,
    navigateToOffer,
    handleToggleOfferStatus,
  } = useOfferList();

  const [targetStatus, setTargetStatus] = useState<string>('staging');

  // Handle status toggle confirmation
  const handleStatusToggleClick = (offer: OfferListItem, target: string) => {
    setSelectedOffer(offer);
    setTargetStatus(target);
    setStatusDialogOpen(true);
  };

  const handleConfirmStatusToggle = async () => {
    if (selectedOffer) {
      const success = await handleToggleOfferStatus(selectedOffer.offer_id, targetStatus);
      if (success) {
        setStatusDialogOpen(false);
        setSelectedOffer(null);
      }
    }
  };

  const handleCancelStatusToggle = () => {
    setStatusDialogOpen(false);
    setSelectedOffer(null);
  };

  // Table columns configuration
  const columns = [
    {
      id: 'offer_id',
      label: 'ID',
      minWidth: 30,
    },
    {
      id: 'offer_name',
      label: 'Offer Name',
      minWidth: 260,
      format: (value: string) => (
        <Typography variant="body2" className="capitalize-first">
          {value}
        </Typography>
      ),
    },
    {
      id: 'scope',
      label: 'Platform',
      minWidth: 150,
      format: (value: number) => (
        <Typography variant="body2" className="capitalize-first">
          {getLabelFromOptionId(value, scopeOptions)}
        </Typography>
      ),
    },
    {
      id: 'price',
      label: 'Price',
      minWidth: 120,
      format: (value: number, row: OfferListItem) => (
        <Typography variant="body2">{formatCurrency(value, row.currency, currencyOptions, priceOptions)}</Typography>
      ),
    },
    {
      id: 'offer_category',
      label: 'Category',
      minWidth: 180,
      format: (value: number | null) => (
        <Typography variant="body2">{getLabelFromOptionId(value, categoryOptions) || 'No category'}</Typography>
      ),
    },
    {
      id: 'quantity',
      label: 'Quantity',
      minWidth: 20,
      format: (value: number) => <Typography variant="body2">{value?.toLocaleString() || '-'}</Typography>,
    },
    {
      id: 'start_date',
      label: 'Start Date',
      minWidth: 126,
      format: (value: string) => <Typography variant="body2">{formatDate(value)}</Typography>,
    },
    {
      id: 'end_date',
      label: 'End Date',
      minWidth: 126,
      format: (value: string) => <Typography variant="body2">{formatDate(value)}</Typography>,
    },
    {
      id: 'sync_on',
      label: 'Environment',
      minWidth: 120,
      align: 'center' as const,
      format: (value: string) => <SyncOnChip value={value} variant="light" size="small" showIcon />,
    },
    {
      id: 'configurations',
      label: 'Configurations',
      minWidth: 140,
      align: 'center' as const,
      sortable: false,
      format: (_value: unknown, row: OfferListItem) => {
        const hasSchedule = row.offer_schedule && row.offer_schedule.length > 0;
        const hasPromotion = row.offer_promotion && row.offer_promotion.length > 0;
        return (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip placement="top" title={hasSchedule ? 'Schedule configured' : 'No schedule'} arrow>
              <Event fontSize="small" color={hasSchedule ? 'success' : 'error'} />
            </Tooltip>
            <Tooltip placement="top" title={hasPromotion ? 'Promotion configured' : 'No promotion'} arrow>
              <Campaign fontSize="small" color={hasPromotion ? 'success' : 'error'} />
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'center' as const,
      sortable: false,
      format: (value, row: OfferListItem) => (
        <ActionButtons
          row={row}
          navigateToOffer={navigateToOffer}
          handleStatusToggle={handleStatusToggleClick}
          theme={theme}
        />
      ),
    },
  ];

  // Remove search from advanced filters
  const appliedFilterCount = [
    filters.status,
    filters.sync_on,
    // filters.dateFrom,
    // filters.dateTo,
    filters.category,
    filters.currency,
    filters.scope,
  ].filter(Boolean).length;

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      {/* Header */}
      <PageHeader
        title="Offers"
        subtitle="Manage your game offers and promotions"
        primaryActionLabel={canAdd && 'Create New Offer'}
        primaryIcon={<AddBox />}
        onPrimaryAction={() => navigateToOffer('/offers/new')}
        secondaryActions={
          (canAdd || canUpdate) && (
            <Button
              variant="outlined"
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              sx={{ height: 40, minWidth: 140 }}
            >
              More Actions
            </Button>
          )
        }
      />

      {/* Controls Toolbar */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <TableSearchBar
              filters={filters}
              categoryOptions={categoryOptions}
              currencyOptions={currencyOptions}
              countryOptions={countryOptions}
              scopeOptions={scopeOptions}
              onChange={changeFilters}
              onFilterClick={() => setDrawerOpen(true)}
              filterCount={appliedFilterCount}
              loading={configLoading || isLoading}
              onOpenColumnsMenu={(anchor) => openColumnsMenuRef.current && openColumnsMenuRef.current(anchor)}
              columnsCount={visibleColsCount}
            />
          </Box>
        </Box>
      </Paper>

      {/* More Actions Menu */}
      <MoreActionsMenu
        anchorEl={actionsAnchorEl}
        onClose={() => setActionsAnchorEl(null)}
        canAdd={canAdd}
        canUpdate={canUpdate}
        onBulkCreate={() => setBulkDialogOpen(true)}
        onBulkSchedule={() => setBulkScheduleOpen(true)}
        filters={filters}
      />

      <OfferFiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        onFilterChange={changeFilters}
        onReset={clearFilter}
        loading={configLoading || isLoading}
        categoryOptions={categoryOptions}
        currencyOptions={currencyOptions}
        scopeOptions={scopeOptions}
        countryOptions={countryOptions}
      />

      {/* Bulk Schedule Drawer */}
      <ScheduleDrawer
        open={bulkScheduleOpen}
        onClose={() => setBulkScheduleOpen(false)}
        bulkMode
        offerOptions={offers}
      />

      {/* Offer Details Drawer */}
      <OfferDetailsDrawer
        open={!!selectedOfferForDetails}
        onClose={() => setSelectedOfferForDetails(null)}
        offer={selectedOfferForDetails}
        onEdit={(offer) => {
          setSelectedOfferForDetails(null);
          navigateToOffer(`/offers/${offer.offer_id}/edit`);
        }}
      />

      {/* Data Table */}
      <DataTable
        columns={columns}
        columnsMenuController={(open, _close, info) => {
          openColumnsMenuRef.current = open;
          setVisibleColsCount(info.getVisibleCount());
        }}
        data={offers}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        onRowsPerPageChange={changePageSize}
        onSort={handleSort}
        orderBy={orderBy}
        order={order}
        emptyMessage="No offers found"
        onRowClick={(offer: OfferListItem) => setSelectedOfferForDetails(offer)}
        columnVisibilityKey="offers_table"
      />

      {/* Status Toggle Confirmation Dialog */}
      <ConfirmDialog
        open={statusDialogOpen}
        title={`Push to ${targetStatus === 'staging' ? 'Sandbox' : 'Production'}`}
        message={
          targetStatus === 'staging'
            ? 'This offer will be saved in sandbox mode. Data will be pushed to the sandbox environment.'
            : 'This offer will be saved in production mode. Data will be pushed to the production environment.'
        }
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleConfirmStatusToggle}
        onCancel={handleCancelStatusToggle}
        loading={isUpdatingStatus}
        severity="info"
      />

      {/* Bulk Offer Dialog */}
      <BulkOfferDrawer open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} />

      {/* AI Chatbot */}
      {/* <FloatingChatButton /> */}
    </Box>
  );
};

export default OfferList;
